import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Zap, FileDown, Copy, Pencil, Loader2, AlertTriangle, MapPin, GitCompare, FileText, ArrowUpRight, Maximize2, Minimize2, X, Trash2, Undo2, Redo2, Check, CloudOff, Signpost } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DEFAULTS, EVENT_TYPES, KINDS, STATUS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import EventMap, { NoEntryMini } from "@/components/map/EventMap";
import { DrawToolbar } from "@/components/map/DrawToolbar";
import { MapLayersPanel } from "@/components/map/MapLayersPanel";
import { STAFF_GROUPS, StaffMini } from "@/components/map/StaffMarkers";
import { DetourPanel, StaffPanel } from "@/components/map/PickPanels";
import { TimeBar, staffAt } from "@/components/map/TimeBar";
import { MeasurePanel, BoxSavePanel, GroupPlacePanel } from "@/components/map/MapTools";
import { MeasureButton } from "@/components/map/DrawToolbar";
import { pathM } from "@/lib/geo";
import { inBox, offsetOf, applyOffset, simplify } from "@/lib/geo";
import { SIGNS, SignMini } from "@/components/map/SignObjects";
import { ElementForm } from "@/components/ElementForm";
import { ShiftsBriefing, defaultShifts, totalsFromShifts } from "@/components/ShiftsBriefing";
import { AssignPanel } from "@/components/plan/AssignPanel";
import { buildUnits, staffPointsFrom, unitPosts, toMin, hm as hmOf, isMine } from "@/lib/staff";
import { EventInfoFields, DuplicateDialog } from "@/components/EventDialogs";
import { StaffGantt } from "@/components/plan/StaffGantt";
import { AiReport } from "@/components/plan/AiReport";
import { PlanSummary, DetoursList, SignageTable } from "@/components/plan/PlanParts";
import { TemplatesPanel, saveTemplate } from "@/components/TemplatesPanel";

const roadName = async ([lat, lon]) => {
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=17&accept-language=es`);
    const j = await r.json();
    return j.address?.road || j.address?.pedestrian || "";
  } catch { return ""; }
};

const ElementList = ({ elements, selectedId, onSelect, timings }) => (
  <div className="space-y-1.5 max-h-[45vh] lg:max-h-none overflow-y-auto pr-1" data-testid="elements-list">
    {elements.length === 0 && <div className="text-xs text-slate-500 py-4 text-center border border-dashed border-slate-200 rounded-lg">Use las herramientas del mapa para marcar recorrido, cortes y zonas.</div>}
    {elements.map((e) => (
      <button key={e.id} onClick={() => onSelect(e.id)} data-testid={`element-item-${e.id}`}
        className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border transition-colors ${selectedId === e.id ? "border-sky-500/50 bg-sky-500/10" : "border-slate-200 bg-slate-50 hover:bg-slate-100"}`}>
        {e.auto_generated ? <NoEntryMini /> : e.kind === "sign" ? <SignMini type={e.sign_type} rot={e.rotation} size={14} /> : <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: KINDS[e.kind].color }} />}
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5 text-xs text-slate-800"><span className="truncate">{e.name || KINDS[e.kind].short}</span>
            {e.auto_generated && <span className={`shrink-0 text-[0.5625rem] px-1.5 rounded border ${e.locked ? "border-rose-400/60 text-rose-800" : "border-rose-400/30 text-rose-700"}`} data-testid={`element-auto-badge-${e.id}`}>{e.locked ? "Aceptado" : "Propuesta"}</span>}
          </span>
          <span className="block font-mono text-[0.625rem] text-slate-500">{e.kind === "sign" ? `${SIGNS[e.sign_type]?.code || "Objeto"}${SIGNS[e.sign_type]?.qty ? ` x${e.qty || 1}` : ""}` : KINDS[e.kind].short}{timings?.[e.id] ? ` · ${timings[e.id].start_hm}–${timings[e.id].end_hm}` : ""}</span>
        </span>
      </button>
    ))}
  </div>
);

// Campos que deshacer/rehacer restauran (todo lo que el usuario edita; el plan se recalcula solo)
const HIST_FIELDS = ["name", "type", "date", "start_time", "end_time", "status", "location", "description", "town", "elements", "assignments",
  "shifts", "resources", "detour_overrides", "post_overrides", "auto_cuts", "auto_cuts_dismissed"];
const snapshot = (e) => Object.fromEntries(HIST_FIELDS.map((k) => [k, e[k]]));
const AUTOSAVE_MS = 1200;
const PLAN_ENGINE = 6; // igual que backend/planner.py: planes de un motor anterior se recalculan al abrir
// Plantilla de recuadro: todo lo dibujado que cae entero dentro (no el recorrido, ni propuestas sin aceptar, ni puestos de personal)
const TEMPLATE_DROP = ["id", "name", "coords", "group_id", "auto_generated", "auto_origin", "locked"]; // el nombre lleva la calle de origen: al colocarla se nombra con la plantilla
const boxContents = (ev, box) => {
  const usable = ev.elements.filter((e) => e.kind !== "route" && e.coords?.length && !(e.auto_generated && !e.locked));
  const inside = usable.filter((e) => e.coords.every((c) => inBox(c, box[0], box[1])));
  const partial = usable.filter((e) => !inside.includes(e) && e.coords.some((c) => inBox(c, box[0], box[1]))).length;
  const posts = (ev.assignments || []).filter((a) => a.loc && inBox(a.loc, box[0], box[1])).length;
  return { inside, partial, posts };
};
const COALESCE_MS = 800; // cambios seguidos (escribir, arrastrar) cuentan como un solo paso de deshacer
const MAX_HIST = 100;
const saveBody = (e) => { const b = { ...e }; delete b.plan; delete b.id; return b; };

const SyncBadge = ({ sync, onRetry }) => {
  if (sync.state === "saving") return <span className="inline-flex items-center gap-1 text-[0.625rem] px-2 py-0.5 rounded-full border border-sky-500/40 text-sky-700" data-testid="sync-badge"><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</span>;
  if (sync.state === "planning") return <span className="inline-flex items-center gap-1 text-[0.625rem] px-2 py-0.5 rounded-full border border-sky-500/40 text-sky-700" data-testid="sync-badge"><Loader2 className="w-3 h-3 animate-spin" /> Recalculando plan…</span>;
  if (sync.state === "pending") return <span className="text-[0.625rem] px-2 py-0.5 rounded-full border border-slate-500/40 text-slate-500" data-testid="sync-badge">Cambios pendientes…</span>;
  if (sync.state === "error") return <button onClick={onRetry} className="inline-flex items-center gap-1 text-[0.625rem] px-2 py-0.5 rounded-full border border-red-500/50 text-red-700 hover:bg-red-500/10" data-testid="sync-badge" title={sync.msg}><CloudOff className="w-3 h-3" /> No se pudo guardar · Reintentar</button>;
  if (sync.at) return <span className="inline-flex items-center gap-1 text-[0.625rem] px-2 py-0.5 rounded-full border border-emerald-500/30 text-emerald-700" data-testid="sync-badge"><Check className="w-3 h-3" /> Guardado {sync.at}</span>;
  return null;
};

export default function EventEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canEdit } = useAuth();
  const [ev, setEv] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [rev, setRev] = useState(0);
  const [sync, setSync] = useState({ state: "idle" });
  const [hist, setHist] = useState({ undo: 0, redo: 0 });
  const evRef = useRef(null);
  const revRef = useRef(0);
  const dirtyRef = useRef(false);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const lastPushRef = useRef(0);
  const runningRef = useRef(false);
  const againRef = useRef(false);
  evRef.current = ev;
  dirtyRef.current = dirty;
  const [mode, setMode] = useState(null);
  const [assignKey, setAssignKey] = useState(null);
  const [focus, setFocus] = useState(null);
  const [draft, setDraft] = useState([]);
  const [sel, setSel] = useState(null);
  const [fit, setFit] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [dup, setDup] = useState(null);
  const [planTab, setPlanTab] = useState("summary");
  const [sideTab, setSideTab] = useState("elements");
  const [briefingNew, setBriefingNew] = useState(false);
  const [oneWays, setOneWays] = useState(false);
  const [base, setBase] = useState(() => localStorage.getItem("map_base_v2") || "light");
  const [hidden, setHidden] = useState([]);
  const [full, setFull] = useState(false);
  const [measure, setMeasure] = useState([]);
  const [box, setBox] = useState([]);
  const [group, setGroup] = useState(null);
  const [pick, setPick] = useState(null);
  const [at, setAt] = useState(null);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { localStorage.setItem("map_base_v2", base); }, [base]);
  const delRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "Delete" || e.key === "Supr") && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) delRef.current?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (!full) return;
    const onKey = (e) => e.key === "Escape" && setFull(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);
  const [oneWayStatus, setOneWayStatus] = useState(null);
  const [sentFeedback, setSentFeedback] = useState([]);
  const loadFeedback = useCallback(() => { api.get(`/events/${id}/detour-feedback`).then((r) => setSentFeedback(r.data.map((x) => x.element_id))).catch(() => {}); }, [id]);
  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => {
      const d = r.data;
      if (!d.shifts?.length) { d.shifts = defaultShifts(d); setBriefingNew(true); setSideTab("briefing"); }
      setEv(d); setFit((f) => f + 1);
      pastRef.current = []; futureRef.current = []; setHist({ undo: 0, redo: 0 });
      // plan pendiente o desfasado al abrir: se recalcula solo
      if (d.elements?.length && (!d.plan || d.updated_at > d.plan.generated_at || (d.plan.engine || 1) < PLAN_ENGINE)) setTimeout(() => syncRef.current?.(true), 300);
    }).catch((e) => { toast.error(errMsg(e)); nav("/"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, nav]);

  const markChanged = () => { revRef.current += 1; setRev(revRef.current); setDirty(true); setSync((s) => (s.state === "saving" || s.state === "planning" ? s : { ...s, state: "pending" })); };
  // Todo cambio del usuario pasa por aquí: guarda un paso de deshacer y programa el guardado automático
  const change = useCallback((fn, { history = true } = {}) => {
    const cur = evRef.current;
    if (history && cur) {
      const now = Date.now();
      if (now - lastPushRef.current > COALESCE_MS || !pastRef.current.length) {
        pastRef.current = [...pastRef.current.slice(-(MAX_HIST - 1)), snapshot(cur)];
      }
      lastPushRef.current = now;
      futureRef.current = [];
      setHist({ undo: pastRef.current.length, redo: 0 });
    }
    setEv((p) => fn(p));
    markChanged();
  }, []);
  const update = useCallback((patch) => change((p) => ({ ...p, ...patch })), [change]);
  const restore = (from, to) => {
    if (!from.current.length || !evRef.current) return;
    const target = from.current[from.current.length - 1];
    from.current = from.current.slice(0, -1);
    to.current = [...to.current, snapshot(evRef.current)];
    lastPushRef.current = 0;
    setEv((p) => ({ ...p, ...target }));
    setSel(null); setPick(null);
    setHist({ undo: pastRef.current.length, redo: futureRef.current.length });
    markChanged();
  };
  const undo = () => restore(pastRef, futureRef);
  const redo = () => restore(futureRef, pastRef);
  const undoRef = useRef(null);
  undoRef.current = { undo, redo };
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.metaKey || e.ctrlKey) || ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); undoRef.current.undo(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); undoRef.current.redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Guardado automático + recálculo del plan. Si llegan cambios mientras trabaja, al terminar repite con lo último.
  const runSync = async (planOnly = false) => {
    if (!canEdit || !evRef.current) return;
    if (runningRef.current) { againRef.current = true; return; }
    runningRef.current = true;
    const startRev = revRef.current;
    try {
      if (!planOnly || dirtyRef.current) {
        setSync((s) => ({ ...s, state: "saving" }));
        const r = await api.put(`/events/${id}`, saveBody(evRef.current));
        setEv((p) => ({ ...p, updated_at: r.data.updated_at }));
        if (revRef.current === startRev) setDirty(false);
      }
      if (evRef.current.elements?.length) {
        setSync((s) => ({ ...s, state: "planning" }));
        const before = evRef.current.elements.filter((e) => e.auto_generated && !e.locked).length;
        const r = await api.post(`/events/${id}/plan`);
        if (revRef.current === startRev) {
          const fresh = await api.get(`/events/${id}`);
          setEv((p) => ({ ...p, plan: r.data, elements: fresh.data.elements, share_token: fresh.data.share_token, updated_at: fresh.data.updated_at }));
          const after = fresh.data.elements.filter((e) => e.auto_generated && !e.locked).length;
          if (after > before) toast.info(`${after - before} corte(s) previo(s) nuevo(s) propuesto(s) para revisar`);
        } else {
          setEv((p) => ({ ...p, plan: r.data, share_token: p.share_token || r.data.share_token }));
        }
        if (r.data.auto_cuts?.status === "no_disponible") toast.warning("No se pudo analizar la red viaria (OpenStreetMap no disponible): sin cortes previos automáticos");
      }
      setSync({ state: "idle", at: new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) });
    } catch (e) {
      setSync({ state: "error", msg: errMsg(e) });
      toast.error(`No se pudo guardar: ${errMsg(e)}`);
    } finally {
      runningRef.current = false;
      if (againRef.current || revRef.current !== startRev) { againRef.current = false; if (dirtyRef.current) setTimeout(() => runSync(), 50); }
    }
  };
  const syncRef = useRef(null);
  syncRef.current = runSync;
  useEffect(() => {
    if (!dirty || !canEdit) return;
    const t = setTimeout(() => syncRef.current(), AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [rev, dirty, canEdit]);
  // Al salir con algo sin guardar: aviso del navegador y último guardado en segundo plano
  useEffect(() => {
    const flush = () => {
      if (!dirtyRef.current || !evRef.current) return;
      fetch(`${API}/events/${id}`, { method: "PUT", credentials: "include", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify(saveBody(evRef.current)) }).catch(() => {});
    };
    const onBefore = (e) => { if (dirtyRef.current || runningRef.current) { flush(); e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onBefore);
    return () => { window.removeEventListener("beforeunload", onBefore); flush(); };
  }, [id]);
  const setElements = (fn) => update({ elements: fn(ev.elements) });
  const buildGroup = (g) => g.tpl.items.map((it, i) => {
    const { offset, offsets, ...props } = it;
    const kind = it.kind || "sign";
    return {
      ...DEFAULTS[kind], ...props, kind, id: `${g.gid}-${i}`, group_id: g.gid,
      start_time: it.start_time || "", end_time: it.end_time || "",
      ...(kind === "sign" ? { qty: it.qty || 1, rotation: ((it.rotation || 0) + g.rot) % 360 } : {}),
      name: it.name || `${g.tpl.name} · ${kind === "sign" ? SIGNS[it.sign_type]?.label || "Objeto" : KINDS[kind]?.short}`,
      coords: (offsets || [offset]).map((o) => applyOffset(g.anchor, o, g.rot)),
    };
  });
  const placeGroup = (g) => {
    setGroup(g);
    if (g.anchor) update({ elements: [...ev.elements.filter((e) => e.group_id !== g.gid), ...buildGroup(g)] });
  };
  const cancelGroup = () => {
    if (group) update({ elements: ev.elements.filter((e) => e.group_id !== group.gid) });
    setGroup(null); setMode(null);
  };
  const saveBoxTemplate = async (name) => {
    const items = boxContents(ev, box).inside;
    const center = [(box[0][0] + box[1][0]) / 2, (box[0][1] + box[1][1]) / 2];
    try {
      await api.post("/templates", {
        kind: "sign_group", name, coords: [center],
        items: items.map((e) => ({
          ...Object.fromEntries(Object.entries(e).filter(([k]) => !TEMPLATE_DROP.includes(k))),
          offsets: e.coords.map((c) => offsetOf(center, c).map((v) => +v.toFixed(2))),
        })),
      });
      window.dispatchEvent(new Event("templates-changed"));
      toast.success(`Plantilla "${name}" guardada con ${items.length} elemento(s)`);
      setBox([]); setMode(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addFromTemplate = (t) => {
    if (t.kind === "sign_group") {
      // Se inserta exactamente donde estaba al guardarla; luego cada elemento se puede mover o editar
      const els = buildGroup({ tpl: t, gid: crypto.randomUUID(), anchor: t.coords[0], rot: 0 });
      update({ elements: [...ev.elements, ...els] });
      setFocus({ loc: t.coords[0], t: Date.now() });
      setSideTab("elements");
      toast.success(`«${t.name}» añadida en su ubicación original · ${els.length} elemento(s)`, { action: { label: "Deshacer", onClick: () => undoRef.current.undo() } });
      return;
    }
    const { id: _tid, created_by, created_at, ...props } = t;
    const existingRoute = t.kind === "route" && ev.elements.find((e) => e.kind === "route");
    const el = { ...props, id: existingRoute ? existingRoute.id : crypto.randomUUID() };
    if (t.kind === "no_parking") { el.start_time = ev.start_time; el.end_time = ev.end_time; }
    if (t.kind === "route") el.start_time = ev.start_time;
    setElements((els) => (existingRoute ? els.map((e) => (e.id === existingRoute.id ? el : e)) : [...els, el]));
    setSel(el.id);
    setSideTab("elements");
    toast.success(`"${t.name}" añadido al evento`);
  };



  const addElement = async (kind, coords, presetName, extra = {}) => {
    const existingRoute = kind === "route" && ev.elements.find((e) => e.kind === "route");
    if (existingRoute) {
      setElements((els) => els.map((e) => (e.id === existingRoute.id ? { ...e, coords } : e)));
      setSel(existingRoute.id);
      toast.info("Recorrido actualizado");
      return;
    }
    const newId = crypto.randomUUID();
    const n = ev.elements.filter((e) => e.kind === kind).length + 1;
    const el = { id: newId, kind, coords, name: presetName || `${KINDS[kind].short} ${n}`, start_time: kind === "route" ? ev.start_time : "", end_time: "", ...DEFAULTS[kind], ...extra };
    if (kind === "no_parking") { el.start_time = ev.start_time; el.end_time = ev.end_time; }
    change((p) => ({ ...p, elements: [...p.elements, el] }));
    setSel(newId);
    setSideTab("elements");
    if (kind !== "route" && !presetName) {
      const road = await roadName(kind === "street_cut" || kind === "special_path" || kind === "reversed_oneway" ? coords[Math.floor(coords.length / 2)] : coords[0]);
      if (road) change((p) => ({ ...p, elements: p.elements.map((e) => (e.id === newId && e.name === el.name ? { ...e, name: kind === "sign" ? `${SIGNS[e.sign_type]?.label || "Objeto"} · ${road}` : kind === "no_parking" ? `Zona ${road}` : kind === "street_cut" ? `Tramo ${road}` : kind === "special_path" ? `Paso especial ${road}` : kind === "reversed_oneway" ? `Sentido invertido ${road}` : `Corte ${road}` } : e)) }), { history: false });
    }
  };

  const onMapClick = (p) => {
    if (mode === "assign") { addPost(p); return; }
    if (mode === "cut_point") { addElement("cut_point", [p]); setMode(null); return; }
    if (mode === "measure") { setMeasure((m) => [...m, p]); return; }
    if (mode === "box") { setBox((b) => (b.length >= 2 ? [p] : [...b, p])); return; }
    if (mode === "place_group" && group) { placeGroup({ ...group, anchor: p }); return; }
    if (mode?.startsWith("sign:")) {
      const t = mode.slice(5);
      addElement("sign", [p], undefined, { sign_type: t, name: `${SIGNS[t].label} ${ev.elements.filter((e) => e.kind === "sign").length + 1}` });
      return;
    }
    setDraft((d) => [...d, p]);
  };
  const finishDraw = () => {
    if (mode === "sign_line") addElement("sign", draft, undefined, { sign_type: "valla_linea", spacing: 2.5, name: `Línea de vallas ${ev.elements.filter((e) => e.sign_type === "valla_linea").length + 1}` });
    else addElement(mode, draft);
    setDraft([]); setMode(null);
  };
  const cancelDraw = () => { setDraft([]); setMode(null); };
  const pickOneWay = (w) => {
    addElement("reversed_oneway", [...w.coords].reverse().map((p) => [+p[0].toFixed(6), +p[1].toFixed(6)]), w.name ? `Sentido invertido ${w.name}` : undefined);
    setMode(null);
    toast.success("Sentido invertido añadido · ajuste el tramo y su horario");
  };

  const generate = () => runSync(!dirty);

  if (!ev) return <div className="p-10 font-mono text-xs text-slate-500" data-testid="editor-loading">CARGANDO EVENTO…</div>;

  const plan = ev.plan;
  const syncing = sync.state === "saving" || sync.state === "planning";
  const stale = plan && (dirty || syncing || ev.updated_at > plan.generated_at);
  const selected = ev.elements.find((e) => e.id === sel);
  const proposals = ev.elements.filter((e) => e.auto_generated && !e.locked);
  const dov = ev.detour_overrides || {};
  const shownDetours = (plan?.detours || []).filter((d) => ev.elements.some((e) => e.id === d.element_id))
    .map((d) => (dov[d.element_id] ? { ...d, coords: dov[d.element_id], manual: true, distance_m: Math.round(pathM(dov[d.element_id])), duration_min: +(pathM(dov[d.element_id]) / 416).toFixed(1), streets: [], entry_point: dov[d.element_id][0] } : d));
  const units = buildUnits(ev.shifts || []);
  const anchor = (toMin(ev.start_time) ?? 600) - 360;
  const assignments = ev.assignments || [];
  const allStaff = staffPointsFrom(units, assignments, anchor);
  const shownStaff = staffAt(allStaff, at);
  const tlist = plan?.tasks || [];
  const slotsAll = allStaff.flatMap((p) => p.slots);
  const trange = slotsAll.length ? [Math.floor(Math.min(...slotsAll.map((s) => s.start)) / 5) * 5, Math.ceil(Math.max(...slotsAll.map((s) => s.end)) / 5) * 5 - 5] : null;
  const assignUnit = units.find((u) => u.key === assignKey);
  const unitPostsSel = assignUnit ? unitPosts(assignUnit, assignments, anchor, units) : [];
  const addPost = (p) => {
    if (!assignUnit) return;
    const mine = assignments.filter((a) => isMine(a, assignUnit, units));
    const near = ev.elements.filter((e) => e.coords?.length && e.kind !== "route").map((e) => ({ e, d: Math.min(...e.coords.map((c) => Math.hypot((c[0] - p[0]) * 110540, (c[1] - p[1]) * 82000))) }))
      .filter((x) => x.d < 60).sort((a, b) => a.d - b.d)[0];
    const a = { id: crypto.randomUUID(), unit_key: assignUnit.key, unit_id: assignUnit.id, loc: p, time: mine.length ? "" : hmOf(assignUnit.shiftFrom), place: near ? near.e.name : "", notes: "" };
    update({ assignments: [...assignments, a] });
    setMode(null);
    toast.success(`Ubicación ${mine.length + 1} asignada a ${assignUnit.tip ? `TIP ${assignUnit.tip}` : assignUnit.id} · indique la hora y las órdenes`);
    if (!near) roadName(p).then((road) => road && change((x) => ({ ...x, assignments: (x.assignments || []).map((y) => (y.id === a.id && !y.place ? { ...y, place: road } : y)) }), { history: false }));
  };
  const selectEl = (id) => { setSel(id); setPick(null); };
  const pickDetour = (id) => { setSel(null); setPick({ type: "detour", id }); };
  const pickStaff = (key) => {
    setSel(null); setPick({ type: "staff", id: key });
    const a = assignments.find((x) => x.id === key);
    const o = a && units.find((u) => isMine(a, u, units));
    if (o) setAssignKey(o.key);
  };
  const pDetour = pick?.type === "detour" && shownDetours.find((d) => d.element_id === pick.id);
  const pStaff = pick?.type === "staff" && (shownStaff.find((p) => p.key === pick.id) || allStaff.find((p) => p.key === pick.id));
  const pickPanel = pDetour ? (
    <DetourPanel d={pDetour} edited={!!dov[pDetour.element_id]} canEdit={canEdit} eventId={id} sent={sentFeedback.includes(pDetour.element_id)} onSent={loadFeedback}
      onEdit={() => update({ detour_overrides: { ...dov, [pDetour.element_id]: simplify(pDetour.coords, 6).map((p) => [+p[0].toFixed(6), +p[1].toFixed(6)]) } })}
      onReset={() => { const n = { ...dov }; delete n[pDetour.element_id]; update({ detour_overrides: n }); }}
      onOpenCut={() => selectEl(pDetour.element_id)} />
  ) : pStaff ? (
    <StaffPanel p={pStaff} at={at} moved={false} canEdit={canEdit} onReset={() => {}}
      onOpenTask={(i) => { const a = assignments.find((x) => pStaff.task_ids.includes(x.id) && (x.place || "Puesto") === pStaff.tasks[i]); const o = a && units.find((u) => isMine(a, u, units)); if (o) { setAssignKey(o.key); document.querySelector("[data-testid=assign-panel]")?.scrollIntoView({ behavior: "smooth", block: "center" }); } }} />
  ) : null;
  const moveStaff = (key, loc) => {
    const p = allStaff.find((x) => x.key === key);
    if (!p) return;
    // with a unit selected, move only its post; otherwise the whole group standing there
    const own = assignUnit && p.task_ids.filter((t) => unitPostsSel.some((x) => x.id === t));
    const ids = own?.length ? own : p.task_ids;
    update({ assignments: assignments.map((a) => (ids.includes(a.id) ? { ...a, loc } : a)) });
  };
  const timings = plan?.timings || {};
  const deleteSelected = () => {
    if (!selected) return;
    update({ elements: ev.elements.filter((e) => e.id !== selected.id), ...(selected.auto_generated ? { auto_cuts_dismissed: [...(ev.auto_cuts_dismissed || []), selected.auto_origin || selected.coords[0]] } : {}) });
    setSel(null);
    toast.success(`«${selected.name || "Elemento"}» eliminado`, { action: { label: "Deshacer", onClick: () => undoRef.current.undo() } });
  };
  delRef.current = canEdit && !mode ? deleteSelected : null;
  const selDetour = selected && plan?.detours?.find((d) => d.element_id === selected.id);
  const elementForm = selected && (
    <>
    {selDetour && !selDetour.coords?.length && selDetour.note && (
      <div className="mb-3 text-[0.6875rem] text-amber-800 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5" data-testid="element-detour-note">{selDetour.note}</div>
    )}
    {selDetour?.coords?.length > 0 && (
      <button onClick={() => pickDetour(selected.id)} className="mb-3 w-full text-left text-[0.6875rem] text-emerald-800 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5 hover:bg-emerald-500/20 transition-colors" data-testid="element-detour-link">
        Desvío: {selDetour.distance_m} m · {selDetour.streets.slice(0, 4).join(" → ")}{selDetour.streets.length > 4 ? "…" : ""}
      </button>
    )}
    <ElementForm key={selected.id} el={selected} readOnly={!canEdit} timing={stale ? null : timings[selected.id]}
      onChange={(n) => setElements((els) => els.map((e) => (e.id === n.id ? n : e)))}
      onDelete={() => deleteSelected()}
      onSaveTemplate={canEdit ? () => saveTemplate(selected) : undefined} />
    </>
  );

  return (
    <div className="p-3 sm:p-4 lg:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-sky-700 mb-2" data-testid="back-to-events-link"><ArrowLeft className="w-3.5 h-3.5" /> Archivo de eventos</Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="event-title">{ev.name}</h1>
            <span className={`text-[0.625rem] px-2 py-0.5 rounded-full border ${STATUS[ev.status]?.cls}`} data-testid="event-status-badge">{STATUS[ev.status]?.label}</span>
            {canEdit && <SyncBadge sync={sync} onRetry={() => runSync()} />}
          </div>
          <div className="font-mono text-xs text-slate-500 mt-1 flex items-center gap-2"><MapPin className="w-3 h-3" />{EVENT_TYPES[ev.type]} · {ev.date} · {ev.start_time}–{ev.end_time} · <b className="text-sky-700" data-testid="event-town-label">{ev.town?.name || "Sin población"}</b>{ev.location ? ` · ${ev.location}` : ""}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && <Button variant="outline" onClick={() => { setInfo({ ...ev }); setInfoOpen(true); }} title="Datos del evento" data-testid="edit-event-info-button"><Pencil className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Datos</span></Button>}
          {canEdit && <Button variant="outline" onClick={() => setDup(ev)} title="Duplicar" data-testid="duplicate-event-button"><Copy className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Duplicar</span></Button>}
          {canEdit && <Button variant="outline" onClick={undo} disabled={!hist.undo} title="Deshacer (Cmd/Ctrl+Z)" data-testid="undo-button"><Undo2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Deshacer</span></Button>}
          {canEdit && <Button variant="outline" size="icon" onClick={redo} disabled={!hist.redo} title="Rehacer (Cmd/Ctrl+Shift+Z)" data-testid="redo-button"><Redo2 className="w-4 h-4" /></Button>}
          <Button variant="outline" onClick={() => nav(`/events/${id}/compare`)} title="Comparar ediciones" data-testid="compare-editions-button"><GitCompare className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Comparar ediciones</span></Button>
          {plan && <Button variant="outline" onClick={() => window.open(`/events/${id}/sheets`, "_blank")} title="Hojas de servicio" data-testid="service-sheets-button"><FileText className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Hojas de servicio</span></Button>}
          <Button variant="outline" onClick={() => window.open(`/events/${id}/carteles`, "_blank")} title="Cartelería" data-testid="carteles-button"><Signpost className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Cartelería</span></Button>
          {plan && <Button variant="outline" onClick={() => window.open(`/events/${id}/print`, "_blank")} title="Exportar PDF" data-testid="export-pdf-plan-button"><FileDown className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Exportar PDF</span></Button>}
          {canEdit && (
            <Button variant="outline" onClick={generate} disabled={syncing || !ev.elements.length} title="El plan se recalcula solo con cada cambio; use este botón solo para forzarlo" data-testid="generate-traffic-plan-button">
              {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 sm:mr-1" />}<span className="hidden sm:inline">Recalcular</span>
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(21rem,27rem)] gap-4 items-start">
        <div className="min-w-0 space-y-4">
        <div className={full ? "fixed inset-0 z-[60] bg-slate-100 isolate" : "h-[62vh] min-h-[22rem] lg:h-[calc(100vh-9.5rem)] rounded-xl overflow-hidden border border-slate-200 shadow-lg relative isolate z-0"} data-testid="event-map">
          {canEdit && <DrawToolbar mode={mode} setMode={(m) => { if (mode === "place_group") cancelGroup(); setMode(m); setDraft([]); setBox([]); if (m === "reversed_oneway") setOneWays(true); }} draft={draft} onFinish={finishDraw} onCancel={cancelDraw} onUndo={() => setDraft((d) => d.slice(0, -1))} />}
          <EventMap elements={ev.elements} detours={shownDetours} timings={timings} selectedId={sel} onSelect={selectEl}
            onSelectDetour={pickDetour} selectedDetour={pDetour ? pDetour.element_id : null}
            onDetourChange={canEdit ? (eid, coords) => update({ detour_overrides: { ...dov, [eid]: coords } }) : undefined}
            onSelectStaff={pickStaff} selectedStaff={pStaff ? pStaff.key : null} onMoveStaff={canEdit ? moveStaff : undefined}
            drawMode={mode} draft={draft} onMapClick={onMapClick} fitTrigger={fit}
            onElementChange={canEdit ? (n) => setElements((els) => els.map((e) => (e.id === n.id ? n : e))) : undefined}
            showOneWays={oneWays} onOneWayStatus={setOneWayStatus} onPickOneWay={pickOneWay} base={base} hidden={hidden} resizeKey={full} measure={measure} box={box} center={ev.town ? [ev.town.lat, ev.town.lng] : undefined} staff={shownStaff}
            posts={unitPostsSel} focus={focus} />
          {trange && !mode && (
            <div className="absolute bottom-[52px] left-1/2 -translate-x-1/2 z-[550]">
              <TimeBar range={trange} at={at} setAt={setAt} playing={playing} setPlaying={setPlaying} />
            </div>
          )}
          {ev.town?.name && (
            <div className="absolute bottom-[88px] right-3 z-[500] flex items-center gap-1.5 bg-white/95 backdrop-blur-md border border-sky-500/30 rounded-lg px-3 py-1.5 text-xs text-sky-800" data-testid="map-town-badge">
              <MapPin className="w-3.5 h-3.5 text-sky-600" /><b>{ev.town.name}</b>{ev.town.province && <span className="text-slate-500">· {ev.town.province}</span>}
            </div>
          )}
          {mode === "measure" && <MeasurePanel pts={measure} onUndo={() => setMeasure((m) => m.slice(0, -1))} onClear={() => setMeasure([])} onClose={() => { setMeasure([]); setMode(null); }} />}
          {mode === "box" && <BoxSavePanel corners={box} contents={box.length === 2 ? boxContents(ev, box) : null} onSave={saveBoxTemplate} onCancel={() => { setBox([]); setMode(null); }} />}
          {mode === "place_group" && group && <GroupPlacePanel tpl={group.tpl} placed={!!group.anchor} rot={group.rot} setRot={(rot) => placeGroup({ ...group, rot })}
            onDone={() => { toast.success(`${group.tpl.items.length} objeto(s) colocados`); setGroup(null); setMode(null); }} onCancel={cancelGroup} />}
          <div className={`absolute top-3 z-[500] flex flex-col items-end gap-1 ${full && (selected || pickPanel) ? "right-[376px]" : "right-3"}`}>
            <button onClick={() => setFull((v) => !v)} data-testid="map-fullscreen-button" title={full ? "Salir de pantalla completa (Esc)" : "Pantalla completa"}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border backdrop-blur-md transition-colors bg-white/95 text-slate-700 border-slate-200 hover:bg-slate-100">
              {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />} {full ? "Salir" : "Pantalla completa"}
            </button>
            <MapLayersPanel base={base} setBase={setBase} hidden={hidden} setHidden={setHidden} />
            <MeasureButton active={mode === "measure"} onClick={() => { setMeasure([]); setDraft([]); if (mode === "place_group") cancelGroup(); setMode(mode === "measure" ? null : "measure"); }} />
            <button onClick={() => setOneWays((v) => !v)} data-testid="toggle-oneways-button"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border backdrop-blur-md transition-colors ${oneWays ? "bg-teal-500 text-slate-950 border-teal-400" : "bg-white/95 text-slate-700 border-slate-200 hover:bg-slate-100"}`}>
              <ArrowUpRight className="w-4 h-4" /> Sentido único
            </button>
            {oneWays && oneWayStatus && <span className="text-[0.625rem] bg-white/95 text-teal-800 px-2 py-1 rounded" data-testid="oneways-status">{oneWayStatus}</span>}
          </div>
          {canEdit && selected && !mode && (
            <div className="absolute top-[100px] left-14 z-[500] bg-white/95 border border-sky-500/30 rounded-lg pl-3 pr-1 py-1 text-[0.625rem] text-sky-800 flex items-center gap-3" data-testid="vertex-edit-hint">
              <span><b className="text-slate-900">{selected.name}</b> · {selected.kind === "cut_point" || (selected.kind === "sign" && selected.sign_type !== "valla_linea") ? "Arrastre el punto para moverlo" : "Arrastre los cuadrados blancos para mover · azules para añadir punto · clic derecho para borrar"}</span>
              <button onClick={deleteSelected} title="Eliminar (tecla Supr)" data-testid="map-delete-selected-button"
                className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/15 text-red-700 hover:bg-red-500/30 text-[0.6875rem] font-semibold transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Eliminar
              </button>
              <button onClick={() => setSel(null)} title="Cerrar" className="p-1 text-slate-500 hover:text-slate-900" data-testid="map-deselect-button"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          <div className="absolute bottom-3 left-3 z-[500] bg-white/95 backdrop-blur-md border border-slate-200 rounded-lg px-3 py-2 hidden sm:flex flex-wrap gap-3 text-[0.625rem] text-slate-700" data-testid="map-legend">
            {Object.entries(KINDS).filter(([k]) => k !== "sign").map(([k, v]) => <span key={k} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} />{v.short}</span>)}
            <span className="flex items-center gap-1"><NoEntryMini />Corte previo</span>
            {Object.entries(STAFF_GROUPS).map(([k, v]) => <span key={k} className="flex items-center gap-1"><StaffMini group={k} size={11} />{v.label}</span>)}
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" />Desvío</span>
            {oneWays && <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-teal-500" />Sentido único</span>}
          </div>
          {full && (selected || pickPanel) && (
            <div className="absolute top-3 right-3 bottom-20 w-[360px] z-[600] bg-white/95 backdrop-blur-md border border-slate-200 rounded-xl p-4 overflow-y-auto rise" data-testid="fullscreen-element-panel">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[0.625rem] tracking-widest text-sky-600">ELEMENTO</span>
                <button onClick={() => { setSel(null); setPick(null); }} className="text-slate-500 hover:text-slate-900" data-testid="fullscreen-panel-close"><X className="w-4 h-4" /></button>
              </div>
              {elementForm || pickPanel}
            </div>
          )}
          {mode === "assign" && assignUnit && (
            <div className="absolute bottom-[100px] left-1/2 -translate-x-1/2 z-[600] bg-amber-400 text-slate-950 text-xs font-semibold rounded-lg px-3 py-2 flex items-center gap-2 shadow-lg" data-testid="assign-map-hint">
              Pulse en el mapa la ubicación de {assignUnit.tip ? `TIP ${assignUnit.tip}` : assignUnit.id}
              <button onClick={() => setMode(null)} className="underline">Cancelar</button>
            </div>
          )}
        </div>
        <AssignPanel units={units} assignments={assignments} anchor={anchor} selKey={assignKey} setSelKey={setAssignKey}
          armed={mode === "assign"} setArmed={(v) => { setDraft([]); setMode(v ? "assign" : null); }} canEdit={canEdit}
          onChange={(a) => update({ assignments: a })} onFocus={(loc) => setFocus({ loc, t: Date.now() })} />
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm lg:sticky lg:top-3 lg:h-[calc(100vh-1.5rem)] lg:overflow-y-auto" data-testid="side-panel">
          <Tabs value={sideTab} onValueChange={setSideTab}>
            <TabsList className="w-full bg-slate-50">
              <TabsTrigger value="elements" className="flex-1" data-testid="tab-elements">Elementos ({ev.elements.length})</TabsTrigger>
              <TabsTrigger value="templates" className="flex-1" data-testid="tab-templates">Plantillas</TabsTrigger>
              <TabsTrigger value="briefing" className="flex-1" data-testid="tab-briefing">Briefing</TabsTrigger>
            </TabsList>
            <TabsContent value="templates" className="mt-4">
              <TemplatesPanel canEdit={canEdit} elements={ev.elements} onAdd={addFromTemplate} />
            </TabsContent>
            <TabsContent value="elements" className="space-y-4 mt-4">
              <label className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer" data-testid="auto-cuts-toggle-row">
                <span><span className="text-xs text-slate-800 block">Cortes previos automáticos</span>
                  <span className="text-[0.625rem] text-slate-500">Al generar el plan, valla el último cruce antes de cada calle afectada</span></span>
                <Switch checked={ev.auto_cuts !== false} disabled={!canEdit} onCheckedChange={(v) => update({ auto_cuts: v })} data-testid="auto-cuts-switch" />
              </label>
              {proposals.length > 0 && (
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2.5 space-y-2" data-testid="auto-cuts-review">
                  <div className="text-xs text-rose-800"><b>{proposals.length}</b> cortes previos propuestos. Revíselos en el mapa: solo cuentan en el plan cuando los acepta.</div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <Button size="sm" className="h-7 bg-emerald-500 hover:bg-emerald-400 text-slate-950" onClick={() => setElements((els) => els.map((e) => (e.auto_generated && !e.locked ? { ...e, locked: true } : e)))} data-testid="auto-cuts-accept-all-button">Aceptar todos</Button>
                      <Button size="sm" variant="outline" className="h-7 text-red-700" onClick={() => { update({ elements: ev.elements.filter((e) => !(e.auto_generated && !e.locked)), auto_cuts_dismissed: [...(ev.auto_cuts_dismissed || []), ...proposals.map((e) => e.auto_origin || e.coords[0])] }); setSel(null); }} data-testid="auto-cuts-reject-all-button">Rechazar todos</Button>
                    </div>
                  )}
                </div>
              )}
              <ElementList elements={ev.elements} selectedId={sel} onSelect={selectEl} timings={stale ? null : timings} />
              {selected || pickPanel ? (
                <div className="border-t border-slate-200 pt-4">
                  {!full && (elementForm || pickPanel)}
                </div>
              ) : ev.elements.length > 0 && <div className="text-xs text-slate-500">Seleccione un elemento para editar horarios y personal.</div>}
            </TabsContent>
            <TabsContent value="briefing" className="mt-4">
              <ShiftsBriefing eventId={id} shifts={ev.shifts} isNew={briefingNew} readOnly={!canEdit} summary={stale ? null : plan?.summary}
                onChange={(s) => { setBriefingNew(false); update({ shifts: s, resources: totalsFromShifts(s) }); }} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <section id="plan-section" className="mt-8 bg-white border border-slate-200 rounded-xl p-6 backdrop-blur-md" data-testid="plan-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <div className="font-mono text-xs tracking-[0.25em] text-sky-600">PLAN DE TRÁFICO</div>
            <h2 className="font-display text-2xl font-bold">Propuesta operativa</h2>
          </div>
          {plan && <div className="font-mono text-[0.6875rem] text-slate-500">Generado {new Date(plan.generated_at).toLocaleString("es-ES")}</div>}
        </div>
        {stale && (sync.state === "error"
          ? <div className="flex items-center gap-2 text-sm text-red-700 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2 mb-4" data-testid="plan-stale-warning"><AlertTriangle className="w-4 h-4" /> No se pudo guardar ni recalcular. <button className="underline" onClick={() => runSync()}>Reintentar</button></div>
          : <div className="flex items-center gap-2 text-sm text-sky-700 bg-sky-500/10 border border-sky-500/30 rounded-lg px-4 py-2 mb-4" data-testid="plan-stale-warning"><Loader2 className="w-4 h-4 animate-spin" /> Actualizando el plan con los últimos cambios…</div>)}
        {!plan ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-10 text-center" data-testid="plan-empty">
            Marque en el mapa el recorrido, los puntos de corte, tramos y zonas sin estacionamiento e indique el personal disponible: el plan se calcula solo con cada cambio.
          </div>
        ) : (
          <Tabs value={planTab} onValueChange={setPlanTab}>
            <TabsList className="bg-slate-50 flex-wrap h-auto">
              <TabsTrigger value="summary" data-testid="plan-tab-summary">Resumen</TabsTrigger>
              <TabsTrigger value="detours" data-testid="plan-tab-detours">Desvíos ({plan.detours.length})</TabsTrigger>
              <TabsTrigger value="staff" data-testid="plan-tab-staff">Cronograma de personal</TabsTrigger>
              <TabsTrigger value="signage" data-testid="plan-tab-signage">Señalización y estacionamiento</TabsTrigger>
              <TabsTrigger value="report" data-testid="plan-tab-report">Orden de servicio (IA)</TabsTrigger>
            </TabsList>
            <TabsContent value="summary" className="mt-5"><PlanSummary plan={plan} elements={ev.elements} /></TabsContent>
            <TabsContent value="detours" className="mt-5"><DetoursList detours={plan.detours} eventId={id} canEdit={canEdit} overrides={ev.detour_overrides || {}} sentIds={sentFeedback} onSent={loadFeedback} /></TabsContent>
            <TabsContent value="staff" className="mt-5"><StaffGantt units={plan.units} eventId={id} shareToken={ev.share_token} /></TabsContent>
            <TabsContent value="signage" className="mt-5"><SignageTable signage={plan.signage} parking={plan.parking} /></TabsContent>
            <TabsContent value="report" className="mt-5">
              <AiReport eventId={id} text={plan.ai_report} canEdit={canEdit} stale={stale} onDone={(t) => setEv((p) => ({ ...p, plan: { ...p.plan, ai_report: t } }))} />
            </TabsContent>
          </Tabs>
        )}
      </section>

      <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg" data-testid="edit-event-dialog">
          <DialogHeader><DialogTitle className="font-display">Datos del evento</DialogTitle></DialogHeader>
          {info && <EventInfoFields value={info} onChange={setInfo} withStatus />}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInfoOpen(false)}>Cancelar</Button>
            <Button data-testid="edit-event-apply-button" onClick={() => { const { name, type, date, start_time, end_time, status, location, description, town } = info; update({ name, type, date, start_time, end_time, status, location, description, town }); setInfoOpen(false); }}>Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DuplicateDialog source={dup} onClose={() => setDup(null)} onDone={(n) => nav(`/events/${n.id}`)} />
    </div>
  );
}
